import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";

interface GenerationProgressProps {
  /** Variante de label selon le contexte d'appel. */
  variant?: "recherche-v1" | "recherche-v2" | "sentence";
}

/**
 * Bandeau d'attente affiche pendant la generation LLM (10-30s typique).
 *
 * Sans feedback de progression, l'utilisateur a l'impression que ca plante.
 * On affiche :
 *  - un spinner permanent ;
 *  - un compteur de secondes ecoulees ;
 *  - une indication de l'etape probable, qui evolue par paliers ;
 *  - un message de reassurance au-dela de 25s (cas tail-latency Mistral).
 *
 * Source du timeout client : `AI_GENERATION_TIMEOUT_MS = 90s` dans `lib/api.ts`
 * (post-mortem 1er juin 2026 — le defaut 15s d'axios coupait la requete avant
 * que Mistral ne reponde, generant un faux "Reseau indisponible").
 */
export function GenerationProgress({
  variant = "recherche-v2",
}: GenerationProgressProps) {
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef<number>(performance.now());

  useEffect(() => {
    const id = setInterval(() => {
      setElapsed(Math.floor((performance.now() - startRef.current) / 1000));
    }, 250);
    return () => clearInterval(id);
  }, []);

  // Etapes graduees pour rassurer sans mentir : ces messages refletent
  // l'ordre reel des operations cote backend (retrieval → LLM → critique).
  // Si la latence reelle s'eloigne de ces paliers, c'est juste indicatif —
  // la generation reste la meme en background.
  const stage = pickStage(elapsed, variant);

  return (
    <div
      className="gsf-surface p-4 flex items-start gap-3"
      role="status"
      aria-live="polite"
      aria-label={`Generation en cours, ${elapsed} secondes ecoulees`}
    >
      <Loader2
        size={18}
        className="animate-spin shrink-0 mt-0.5"
        style={{ color: "var(--ai-violet)" }}
      />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">{stage.title}</div>
        {stage.subtitle && (
          <div
            className="text-xs mt-1"
            style={{ color: "var(--arbitre-slate-400)" }}
          >
            {stage.subtitle}
          </div>
        )}
      </div>
      <div
        className="text-xs tabular-nums shrink-0"
        style={{ color: "var(--arbitre-slate-400)" }}
        title="Temps ecoule depuis l'envoi de la requete"
      >
        {elapsed}s
      </div>
    </div>
  );
}

interface Stage {
  title: string;
  subtitle?: string;
}

function pickStage(elapsed: number, variant: GenerationProgressProps["variant"]): Stage {
  // Paliers calibres sur les latences observees en prod :
  // - retrieval Qdrant     : < 2s
  // - generation Mistral   : 5-25s
  // - critique service     : +2-5s (v2 uniquement)
  if (elapsed < 5) {
    return { title: "Recherche dans le corpus OHADA / CCJA…" };
  }
  if (elapsed < 15) {
    return {
      title:
        variant === "sentence"
          ? "Rédaction de la section de sentence…"
          : "Génération du raisonnement juridique…",
    };
  }
  if (elapsed < 25) {
    if (variant === "recherche-v2") {
      return {
        title: "Vérification des citations atomiques…",
        subtitle: "Chaque claim est confronté à ses sources.",
      };
    }
    return { title: "Finalisation de la réponse…" };
  }
  if (elapsed < 60) {
    return {
      title: "Génération en cours…",
      subtitle: "Le moteur prend un peu plus de temps que prévu pour cette question.",
    };
  }
  // 60s+ : on s'approche du timeout 90s, prevenir explicitement
  return {
    title: "Génération longue détectée…",
    subtitle: `${90 - elapsed}s restantes avant abandon automatique. Patience ou reformulez plus simplement.`,
  };
}
